import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Settings, Check, X } from 'lucide-react';
import { ROUTES } from '../constants';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const STORAGE_KEY = 'cookieConsent';

function getStoredPreferences(): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
}

function storePreferences(prefs: CookiePreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

function enableAnalytics() {
  if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
    (window as any).gtag('consent', 'update', {
      analytics_storage: 'granted',
    });
  }
}

function disableAnalytics() {
  if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
    (window as any).gtag('consent', 'update', {
      analytics_storage: 'denied',
    });
  }
}

function enableMarketing() {
  if (typeof window !== 'undefined' && typeof (window as any).fbq === 'function') {
    (window as any).fbq('consent', 'grant');
  }
}

function disableMarketing() {
  if (typeof window !== 'undefined' && typeof (window as any).fbq === 'function') {
    (window as any).fbq('consent', 'revoke');
  }
}

function applyPreferences(prefs: CookiePreferences) {
  if (prefs.analytics) {
    enableAnalytics();
  } else {
    disableAnalytics();
  }
  if (prefs.marketing) {
    enableMarketing();
  } else {
    disableMarketing();
  }
}

export const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = getStoredPreferences();
    if (stored) {
      applyPreferences(stored);
      setPreferences(stored);
    } else {
      disableAnalytics();
      disableMarketing();
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    const prefs: CookiePreferences = { essential: true, analytics: true, marketing: true };
    storePreferences(prefs);
    applyPreferences(prefs);
    setPreferences(prefs);
    setVisible(false);
  }, []);

  const handleEssentialOnly = useCallback(() => {
    const prefs: CookiePreferences = { essential: true, analytics: false, marketing: false };
    storePreferences(prefs);
    applyPreferences(prefs);
    setPreferences(prefs);
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    storePreferences(preferences);
    applyPreferences(preferences);
    setVisible(false);
    setShowSettings(false);
  }, [preferences]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slideUp">
      <div className="bg-gray-900 border-t border-gray-700 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          {!showSettings ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Shield className="h-5 w-5 text-teal-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-200 leading-relaxed">
                    We use cookies to improve your experience on our platform. Essential cookies are
                    required for core functionality. Analytics and marketing cookies help us improve
                    our services.{' '}
                    <Link to={ROUTES.COOKIE_POLICY} className="text-teal-400 hover:text-teal-300 underline">
                      Learn more
                    </Link>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 hover:text-white transition-colors flex-1 sm:flex-none"
                >
                  <Settings className="h-3.5 w-3.5" />
                  Settings
                </button>
                <button
                  onClick={handleEssentialOnly}
                  className="px-3 py-2 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 hover:text-white transition-colors flex-1 sm:flex-none"
                >
                  Essential Only
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-teal-700 rounded-lg hover:bg-teal-600 transition-colors flex-1 sm:flex-none"
                >
                  <Check className="h-3.5 w-3.5" />
                  Accept All
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-white">Cookie Settings</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-white">Essential Cookies</p>
                    <p className="text-xs text-gray-400 mt-0.5">Required for authentication and core functionality</p>
                  </div>
                  <div className="px-3 py-1 text-xs font-medium text-teal-400 bg-teal-900/30 rounded-full">
                    Always On
                  </div>
                </div>
                <div className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-white">Analytics Cookies</p>
                    <p className="text-xs text-gray-400 mt-0.5">Help us understand how visitors use the platform</p>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                    className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                      preferences.analytics ? 'bg-teal-600' : 'bg-gray-600'
                    }`}
                    role="switch"
                    aria-checked={preferences.analytics}
                    aria-label="Toggle analytics cookies"
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                        preferences.analytics ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                <div className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-white">Marketing Cookies</p>
                    <p className="text-xs text-gray-400 mt-0.5">Used to measure advertising effectiveness</p>
                  </div>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                    className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                      preferences.marketing ? 'bg-teal-600' : 'bg-gray-600'
                    }`}
                    role="switch"
                    aria-checked={preferences.marketing}
                    aria-label="Toggle marketing cookies"
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                        preferences.marketing ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleEssentialOnly}
                  className="px-3 py-2 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 hover:text-white transition-colors"
                >
                  Essential Only
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-teal-700 rounded-lg hover:bg-teal-600 transition-colors"
                >
                  <Check className="h-3.5 w-3.5" />
                  Save Preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
